import React from "react";
import { Link } from "react-router-dom";
import LegalFooterBannerAndLinks from "components/legal/LegalFooterBannerAndLinks";
import LegalPagesBrandHeader from "components/legal/LegalPagesBrandHeader";

const PRIZE_ROWS = [
  { rank: "1st", prize: "Alpha subscription offered", threshold: "45" },
  { rank: "2nd", prize: "Beta subscription offered", threshold: "30" },
  { rank: "3rd", prize: "Gama subscription offered", threshold: "20" },
  { rank: "4th", prize: "Delta subscription offered", threshold: "10" },
  { rank: "5th", prize: "Delta subscription offered", threshold: "10" },
];

const STEPS = [
  {
    title: "Share your link",
    text: "Copy your personal referral link from your client dashboard. It uses your Client ID.",
  },
  {
    title: "Friend signs up",
    text: "They register through your link on Path 1 (broker partner). Their country does not matter.",
  },
  {
    title: "They activate",
    text: "They complete broker partner verification and hold an active paid subscription on Path 1.",
  },
  {
    title: "You earn a point",
    text: "Each validated referral counts as 1 point toward your country leaderboard for the active contest.",
  },
];

function SectionCard({ title, subtitle, children, className = "" }) {
  return (
    <section
      className={`rounded-2xl border border-white/10 bg-white/[0.04] p-6 sm:p-8 ${className}`.trim()}
    >
      {title ? (
        <h2 className="text-xl font-bold text-white sm:text-2xl">{title}</h2>
      ) : null}
      {subtitle ? (
        <p className="mt-2 text-sm leading-relaxed text-gray-400">{subtitle}</p>
      ) : null}
      <div className={title || subtitle ? "mt-5" : ""}>{children}</div>
    </section>
  );
}

function BulletList({ items }) {
  return (
    <ul className="space-y-2.5 text-sm leading-relaxed text-gray-300">
      {items.map((item) => (
        <li key={item} className="flex gap-2.5">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-400" aria-hidden />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export default function ReferralContest() {
  return (
    <main className="min-h-screen bg-[#0B1437] px-5 py-6 text-white sm:py-8">
      <div className="mx-auto w-full max-w-5xl">
        <LegalPagesBrandHeader />

        <header className="text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-brand-300">
            Referral Contest
          </p>
          <h1 className="mt-3 text-3xl font-bold sm:text-4xl">
            Country referral contest — one edition per country
          </h1>
          <p className="mx-auto mt-4 max-w-3xl text-sm leading-relaxed text-gray-300 sm:text-base">
            Refer traders to AlphaSniper on Path 1 (broker partner). Climb your country
            leaderboard. The top 5 qualified referrers win subscription prizes delivered as
            unique promo codes.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/SignUp"
              className="inline-flex rounded-xl bg-brand-500 px-8 py-3 text-sm font-bold text-white shadow-lg shadow-brand-500/25 transition hover:bg-brand-600"
            >
              Join &amp; get your link
            </Link>
          </div>
        </header>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              value: "1×",
              label: "Per country",
              detail: "Each eligible country runs this contest once — not every month.",
            },
            {
              value: "50+",
              label: "Active clients",
              detail: "Country opens when 50 verified broker-partner subscribers are counted.",
            },
            {
              value: "5",
              label: "Winners",
              detail: "Alpha, Beta, Gama, Delta, Delta — rank and minimum referrals required.",
            },
            {
              value: "Path 1",
              label: "Broker partner",
              detail: "Referrers, referrals, and prizes all use Path 1 (WLCMBP checkout).",
            },
          ].map((item) => (
            <div
              key={item.label}
              className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 text-center sm:text-left"
            >
              <p className="text-2xl font-extrabold text-white">{item.value}</p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-brand-300">
                {item.label}
              </p>
              <p className="mt-2 text-xs leading-relaxed text-gray-400">{item.detail}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 space-y-6">
          <SectionCard
            title="How it works"
            subtitle="Four steps from share to leaderboard point."
          >
            <ol className="grid gap-4 sm:grid-cols-2">
              {STEPS.map((step, index) => (
                <li
                  key={step.title}
                  className="rounded-xl border border-white/10 bg-white/[0.03] p-4"
                >
                  <p className="text-xs font-bold uppercase tracking-wide text-brand-300">
                    Step {index + 1}
                  </p>
                  <p className="mt-1 font-semibold text-white">{step.title}</p>
                  <p className="mt-2 text-sm leading-relaxed text-gray-400">{step.text}</p>
                </li>
              ))}
            </ol>
          </SectionCard>

          <SectionCard
            title="Country eligibility"
            subtitle="Snapshot taken on the 1st of the month at 00:05 UTC."
          >
            <BulletList
              items={[
                "A country joins the contest when it reaches at least 50 clients with an active paid Path 1 subscription and a verified broker partner account.",
                "If a country is below 50 on snapshot day, there is no contest that month. It may open the month it first reaches 50.",
                "Each country runs this contest only once. Example: if Morocco runs in July, it will not run again in August.",
                "Your referrer country is set at signup (ISO code) and cannot be changed.",
              ]}
            />
          </SectionCard>

          <SectionCard
            title="Who can participate"
            subtitle="Requirements for referrers and referred clients."
          >
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wide text-sky-300">
                  Referrer (you)
                </h3>
                <BulletList
                  items={[
                    "Active AlphaSniper account on Path 1.",
                    "Verified broker partner account.",
                    "Active paid subscription.",
                    "Ranked in your signup country.",
                    "One natural person = one referrer account.",
                  ]}
                />
              </div>
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wide text-sky-300">
                  Referred client
                </h3>
                <BulletList
                  items={[
                    "Registers through your referral link during the active contest period.",
                    "May live in any country.",
                    "Must verify broker partner account on Path 1.",
                    "Must hold an active paid subscription to count as 1 point.",
                    "No self-referrals or linked duplicate accounts.",
                  ]}
                />
              </div>
            </div>
          </SectionCard>

          <SectionCard
            title="Prizes & minimum referrals"
            subtitle="You need both the correct rank and the minimum validated referrals."
          >
            <div className="overflow-x-auto">
              <table className="w-full min-w-[480px] text-left text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-xs uppercase tracking-wide text-gray-400">
                    <th className="pb-3 pr-4 font-semibold">Rank</th>
                    <th className="pb-3 pr-4 font-semibold">Prize</th>
                    <th className="pb-3 font-semibold">Min. referrals</th>
                  </tr>
                </thead>
                <tbody className="text-gray-200">
                  {PRIZE_ROWS.map((row) => (
                    <tr key={row.rank} className="border-b border-white/5">
                      <td className="py-3 pr-4 font-semibold text-white">{row.rank}</td>
                      <td className="py-3 pr-4">{row.prize}</td>
                      <td className="py-3 tabular-nums text-emerald-300">{row.threshold}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-sm text-gray-400">
              Prize durations: Alpha 12 months · Beta 6 months · Gama 3 months · Delta 1 month.
              If no one qualifies for a rank, that prize is not awarded.
            </p>
          </SectionCard>

          <SectionCard
            title="Leaderboard & contest end"
            subtitle="One independent ranking per eligible country."
          >
            <BulletList
              items={[
                "1 validated referral = 1 point. Points update as referrals are validated.",
                "Everyone can move up or down until final closure.",
                "Tie-break order: earliest last validated referral → first to reach the score → supervised random draw.",
                "Normal closure: #1 has at least 45 referrals and is not tied with #2.",
                "If #1 is below 45 and not tied with #2 at month end: Alpha is not awarded; other prizes may still be awarded if qualified.",
              ]}
            />
          </SectionCard>

          <SectionCard
            title="Overtime extension"
            subtitle="Only when the top two are tied below the Alpha threshold."
          >
            <BulletList
              items={[
                "If #1 and #2 have the same score and neither has 45 referrals at scheduled month end, the contest enters overtime (up to 14 extra days).",
                "Overtime is the same edition — e.g. a July contest extended into August remains the July edition.",
                "During overtime the full leaderboard stays open; all referrers can still gain or lose positions.",
                "Overtime ends when #1 reaches 45 referrals and is no longer tied with #2.",
                "After 14 days: standard tie-break rules apply; Alpha may remain unawarded if #1 is still below 45.",
              ]}
            />
          </SectionCard>

          <SectionCard
            title="Prize delivery"
            subtitle="Unique one-time promo codes on Path 1 only."
          >
            <BulletList
              items={[
                "Each winner receives one unique promo code tied to their account.",
                "Codes are used once on Path 1 checkout (WLCMBP partner offer logic).",
                "If you already have an active Path 1 slot, the prize extends that slot — no second slot is created.",
                "Codes expire 60 days after winner notification. Unused codes are forfeited.",
                "Top 3 referrers in each country may be manually reviewed before codes are issued.",
              ]}
            />
          </SectionCard>

          <SectionCard
            title="After your country’s contest"
            subtitle="Referral links stay active — prizes do not."
          >
            <BulletList
              items={[
                "Once a country has completed its single edition, that country will not run the contest again.",
                "You may still share your referral link afterward, but no contest prizes apply for that country.",
                "Referrals outside an active contest period are tracked for internal stats only and do not earn contest points.",
              ]}
            />
          </SectionCard>

          <SectionCard
            title="Fair play & disqualification"
            subtitle="AlphaSniper may reject referrals or remove participants to protect the contest."
          >
            <BulletList
              items={[
                "Multi-account abuse, fake broker verification, referral farms, or manipulation may lead to disqualification.",
                "Disqualified referrers are removed from the leaderboard; suspicious referrals may be voided.",
                "If a winner is disqualified before code use, the prize may pass to the next qualified referrer.",
                "Admin decisions are final except for manifest errors.",
              ]}
            />
          </SectionCard>

          <SectionCard
            title="Important notes"
            subtitle="Please read before participating."
          >
            <BulletList
              items={[
                "Participation is limited to users aged 18 and over.",
                "Prizes are subscription services — not convertible to cash.",
                "AlphaSniper may suspend or cancel the contest in case of abuse or force majeure.",
                "Full legal terms will be published on the official referral rules page when the contest goes live.",
              ]}
            />
          </SectionCard>
        </div>

        <div className="mt-12 flex justify-center px-2">
          <Link
            to="/SignUp"
            className="inline-flex w-full max-w-xl items-center justify-center rounded-2xl bg-brand-500 px-10 py-5 text-center text-lg font-bold text-white shadow-lg shadow-brand-500/25 transition hover:bg-brand-600 sm:text-xl"
          >
            Join Us Now
          </Link>
        </div>

        <footer className="mt-8 border-t border-white/10 pt-5 text-center text-xs text-gray-400 sm:text-sm">
          <p>AlphaSniper 2026 © All Rights Reserved</p>
          <LegalFooterBannerAndLinks />
        </footer>
      </div>
    </main>
  );
}
